import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, switchMap } from 'rxjs';
import {
  AccountCredentialsData,
  AccountData,
  ConfirmPhoneNumberDto,
  CountryDto,
  CreateAccount,
  SendConfirmationEmailDto,
  UserStatusEnum
} from '../models/signup.models';
import { environment } from '../../../../environments/environment';
import { OAuthService } from 'angular-oauth2-oidc';
import { Router } from '@angular/router';
import { IDirectoryResponseData } from '../../../types/directory';
import { Company, User } from '@app/shared/models';
import { UserService } from '@app/services/user.service';
import { CompanyStatus } from '@app/shared/enums/company.enums';

export enum SignupActiveScreen {
  CreateAccount = 1,
  ConfirmEmail = 2,
  RegisterCompany = 3,
  Documents = 4,
  Categories = 5
  // ConfirmPhoneNumber = 4,
}

@Injectable({
  providedIn: 'root'
})
export class SignupService {
  private accountApi = environment.accountApi;
  private accountApiSignupBaseUrl = `${environment.accountApi}/signup`;
  private staticDataDirectoriesApiBaseUrl = `${environment.staticDataApi}/directories`;

  private _isLoading = new BehaviorSubject<boolean>(false);
  public readonly isLoading$: Observable<boolean> =
    this._isLoading.asObservable();
  private _errorMessages = new BehaviorSubject<string[] | undefined>(undefined);
  public readonly errorMessages$: Observable<string[] | undefined> =
    this._errorMessages.asObservable();
  public signupActiveScreen = new BehaviorSubject<SignupActiveScreen>(
    SignupActiveScreen.CreateAccount
  );
  public readonly signupActiveScreen$: Observable<SignupActiveScreen> =
    this.signupActiveScreen.asObservable();
  private _countries = new BehaviorSubject<CountryDto[]>([]);
  public readonly countries$: Observable<CountryDto[]> =
    this._countries.asObservable();
  private _user = new BehaviorSubject<User | undefined>(undefined);
  public readonly user$: Observable<User | undefined> =
    this._user.asObservable();

  private _accountData = new BehaviorSubject<AccountData>({
    email: '',
    privacyPolicy: false,
    termsOfUsage: false,
    firstName: '',
    lastName: ''
  });

  constructor(
    private httpClient: HttpClient,
    private oauthService: OAuthService,
    private router: Router,
    private userService: UserService
  ) {}

  public getCountries() {
    this._isLoading.next(true);
    const query = this.httpClient.get<IDirectoryResponseData>(
      `${this.staticDataDirectoriesApiBaseUrl}/countries`
    );

    query
      .pipe(
        catchError(error => {
          throw error;
        })
      )
      .subscribe({
        next: data => {
          this._errorMessages.next(undefined);

          this._countries.next(data.dataList as unknown as CountryDto[]);

          this._isLoading.next(false);
        },
        error: ({ error }) => {
          this._errorMessages.next(error);
          this._isLoading.next(false);
        }
      });
  }

  public fetchUserSignupActiveScreen = () => {
    this._isLoading.next(true);
    const query = this.userService.getUser();

    query
      .pipe(
        catchError(error => {
          throw error;
        })
      )
      .subscribe({
        next: async data => {
          this._errorMessages.next(undefined);

          this._user.next(data);

          switch (data.status) {
            case UserStatusEnum.PendingCompanyRegistration:
              this.signupActiveScreen.next(SignupActiveScreen.RegisterCompany);
              break;
            // case UserStatusEnum.PendingPhoneNumberConfirmation:
            //   this.signupActiveScreen.next(
            //     SignupActiveScreen.ConfirmPhoneNumber
            //   );
            //   break;
            case UserStatusEnum.Active:
              this.router.navigate(['/company']);
              break;

            default:
              break;
          }

          this._isLoading.next(false);
        },
        error: () => {
          this._isLoading.next(false);
        }
      });
  };

  public sendConfirmationEmail(
    form: SendConfirmationEmailDto,
    accountData: AccountData
  ) {
    this._isLoading.next(true);
    const query = this.httpClient.post<boolean>(
      `${this.accountApiSignupBaseUrl}/email-confirmation`,
      form
    );

    query
      .pipe(
        catchError(error => {
          throw error;
        })
      )
      .subscribe({
        next: () => {
          this._errorMessages.next(undefined);

          this._accountData.next(accountData);
          this.signupActiveScreen.next(SignupActiveScreen.ConfirmEmail);

          this._isLoading.next(false);
        },
        error: ({ error }) => {
          this._errorMessages.next(error);
          this._isLoading.next(false);
        }
      });
  }

  public createAccount(credentials: AccountCredentialsData) {
    this._isLoading.next(true);

    const form = {
      ...this._accountData.value,
      ...credentials
    } as CreateAccount;

    const query = this.httpClient.post<boolean>(
      `${this.accountApiSignupBaseUrl}`,
      form
    );

    query
      .pipe(
        catchError(error => {
          throw error;
        })
      )
      .subscribe({
        next: async () => {
          this._errorMessages.next(undefined);

          await this.oauthService
            .fetchTokenUsingPasswordFlow(
              this._accountData.value?.email as string,
              credentials.password as string
            )
            .then(() => {
              this.userService.getUser().subscribe(() => {
                this.signupActiveScreen.next(
                  SignupActiveScreen.RegisterCompany
                );
                this._isLoading.next(false);
              });
            });
        },
        error: ({ error }) => {
          this._errorMessages.next(error);
          this._isLoading.next(false);
        }
      });
  }

  public editCompany(requestBody: Company) {
    this._isLoading.next(true);

    const query = this.httpClient.put<boolean>(
      `${this.accountApi}/Companies/${requestBody.id}/info`,
      requestBody
    );
    query
      .pipe(
        switchMap(() =>
          this.putCompanyStatus(
            requestBody.id,
            CompanyStatus.PendingUploadingFiles
          )
        ),
        catchError(error => {
          throw error;
        })
      )
      .subscribe({
        next: () => {
          this._errorMessages.next(undefined);
          this.fetchUserSignupActiveScreen();
          this.signupActiveScreen.next(SignupActiveScreen.Documents);
          this._isLoading.next(false);
        },
        error: ({ error }) => {
          this._errorMessages.next(error);
          this._isLoading.next(false);
        }
      });
  }

  public confirmPhoneNumber(form: ConfirmPhoneNumberDto) {
    this._isLoading.next(true);
    const query = this.httpClient.post<boolean>(
      `${this.accountApiSignupBaseUrl}/confirm-phone-number`,
      form
    );

    query
      .pipe(
        catchError(error => {
          throw error;
        })
      )
      .subscribe({
        next: async () => {
          this._errorMessages.next(undefined);

          this.router.navigate(['/company']);

          this._isLoading.next(false);
        },
        error: ({ error }) => {
          this._errorMessages.next(error);
          this._isLoading.next(false);
        }
      });
  }

  public skipPhoneNumberConfirmation() {
    this._isLoading.next(true);
    const query = this.httpClient.get<boolean>(
      `${this.accountApiSignupBaseUrl}/confirm-phone-number/skip`
    );

    query
      .pipe(
        catchError(error => {
          throw error;
        })
      )
      .subscribe({
        next: async () => {
          this._errorMessages.next(undefined);

          this.router.navigate(['/company']);

          this._isLoading.next(false);
        },
        error: ({ error }) => {
          this._errorMessages.next(error);
          this.router.navigate(['/company']);
          this._isLoading.next(false);
        }
      });
  }

  public findCompany(bussinessCode: string): Observable<Company> {
    const params = new HttpParams({ fromObject: { bussinessCode } });
    return this.httpClient.post<Company>(
      `${this.accountApiSignupBaseUrl}/company`,
      null,
      { params }
    );
  }

  public putCompanyStatus(
    companyId: string,
    status: CompanyStatus
  ): Observable<Company> {
    const params = new HttpParams({ fromObject: { companyId, status } });
    return this.httpClient.put<Company>(
      `${this.accountApi}/companies/company/status`,
      null,
      { params }
    );
  }
}
